/**
 * Browser → BFF interaction-context client.
 *
 * Calls the BFF proxy (`GET /api/genesys/interaction/:conversationId`). The BFF
 * reads the agent's Genesys token from the SERVER-SIDE session (#343 — the
 * browser holds no token and forwards no header) and performs the Genesys
 * Conversations call server-side (avoiding CORS), returning the typed banner
 * shape.
 *
 * IDOR authorization is enforced server-side: the BFF resolves the caller's own
 * Genesys user id from the session token and only returns the context if that
 * user is a PARTICIPANT of the requested conversation (else 403). The browser
 * forwards no authorization hint — the path id + the session cookie suffice.
 */

/** Banner context returned by the BFF interaction proxy. */
export interface InteractionBannerContext {
  ani?: string;
  customerName?: string;
  queue?: string;
  conversationId?: string;
  attributes: Record<string, string>;
  cliMatch?: string;
  callsInLastWeek?: number;
}

/** Fetches the interaction context for a conversation from the BFF. */
export async function fetchInteraction(
  conversationId: string,
): Promise<InteractionBannerContext> {
  const res = await fetch(
    `/api/genesys/interaction/${encodeURIComponent(conversationId)}`,
    {
      credentials: 'include',
    },
  );
  if (!res.ok) {
    throw new Error(`interaction fetch failed: ${res.status}`);
  }
  return (await res.json()) as InteractionBannerContext;
}
